import { FieldBase } from '@/components/form';
import { FieldGroup } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { useAnlageassistentContext } from '@/domain/mitarbeiter/features/anlageassistent/context/useAnlageassistentContext';
import { useMitarbeiterPersonalIds } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useMitarbeiterPersonalIds/useMitarbeiterPersonalIds';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line max-lines-per-function
export function Person() {
  const { currentStandort } = useStandortContext();
  const { ctx } = useAnlageassistentContext();
  const form = useAnlageassistentFormContext();
  const { t } = useTranslation();
  const { e2nIds, personalnummern } = useMitarbeiterPersonalIds(
    currentStandort.id,
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h2 className="text-neutrals-900 text-xl font-bold leading-7">
          {ctx.getCurrentSubStep()?.title}
        </h2>
        <p className="text-neutrals-800 text-sm font-normal leading-5">
          {t(
            'domain.mitarbeiter.create.steps.person.persoenliches.description',
          )}
        </p>
      </div>

      <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        <FieldGroup>
          <form.AppField name="person.persoenliches.anrede">
            {(field) => {
              const isInvalid = !field.state.meta.isValid;
              return (
                <FieldBase
                  label={t(
                    'domain.mitarbeiter.create.steps.person.persoenliches.form.anrede.label',
                  )}
                  isRequired>
                  <Select
                    name={field.name}
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    aria-invalid={isInvalid}>
                    <SelectTrigger
                      aria-invalid={isInvalid}
                      id={field.name}
                      className="border-neutrals-300">
                      <SelectValue
                        onBlur={field.handleBlur}
                        placeholder={t(
                          'domain.mitarbeiter.create.steps.person.persoenliches.form.anrede.placeholder',
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Frau">
                        {' '}
                        {t(
                          'domain.mitarbeiter.create.steps.person.persoenliches.form.anrede.options.Frau',
                        )}
                      </SelectItem>
                      <SelectItem value="Herr">
                        {t(
                          'domain.mitarbeiter.create.steps.person.persoenliches.form.anrede.options.Herr',
                        )}
                      </SelectItem>
                      <SelectItem value="divers">
                        {t(
                          'domain.mitarbeiter.create.steps.person.persoenliches.form.anrede.options.divers',
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FieldBase>
              );
            }}
          </form.AppField>
        </FieldGroup>

        <FieldGroup className="col-span-2 grid md:grid-cols-2 items-start">
          <form.AppField name="person.persoenliches.vorname">
            {(field) => (
              <field.TextInput
                label={t(
                  'domain.mitarbeiter.create.steps.person.persoenliches.form.firstname.label',
                )}
                isRequired
                maxLength={50}
              />
            )}
          </form.AppField>
          <form.AppField name="person.persoenliches.nachname">
            {(field) => (
              <field.TextInput
                label={t(
                  'domain.mitarbeiter.create.steps.person.persoenliches.form.lastname.label',
                )}
                isRequired
                maxLength={50}
              />
            )}
          </form.AppField>
        </FieldGroup>
        <FieldGroup className="col-span-2 grid md:grid-cols-2">
          <form.AppField name="person.persoenliches.rufname">
            {(field) => (
              <field.TextInput
                label={t(
                  'domain.mitarbeiter.create.steps.person.persoenliches.form.rufname.label',
                )}
                maxLength={50}
              />
            )}
          </form.AppField>
        </FieldGroup>
        <FieldGroup className="col-span-2 grid md:grid-cols-2 items-start">
          <form.AppField
            name="person.persoenliches.personalnummer"
            validators={{
              onChange: (val) => {
                const duplicate = personalnummern.find(
                  (ids) => ids.personalnummer === val.value,
                );
                return duplicate
                  ? {
                      message: t(
                        'domain.mitarbeiter.create.steps.person.persoenliches.form.personalnummer.validationErrorDuplicate',
                        { name: `${duplicate.vorname} ${duplicate.nachname}` },
                      ),
                    }
                  : undefined;
              },
            }}>
            {(field) => (
              <field.TextInput
                label={t(
                  'domain.mitarbeiter.create.steps.person.persoenliches.form.personalnummer.label',
                )}
                maxLength={50}
                infoText={t(
                  'domain.mitarbeiter.create.steps.person.persoenliches.form.personalnummer.info',
                )}
              />
            )}
          </form.AppField>
          <form.AppField
            name="person.persoenliches.e2nId"
            validators={{
              onChange: (val) => {
                const normalizedValue = val.value?.trim();
                if (!normalizedValue) {
                  return undefined;
                }
                const duplicate = e2nIds
                  .filter((ids) => !!ids.e2nId && ids.e2nId.trim() !== '')
                  .find((ids) => Number(ids.e2nId) === Number(normalizedValue));
                return duplicate
                  ? {
                      message: t(
                        'domain.mitarbeiter.create.steps.person.persoenliches.form.e2nId.validationErrorDuplicate',
                        { name: `${duplicate.vorname} ${duplicate.nachname}` },
                      ),
                    }
                  : undefined;
              },
            }}>
            {(field) => (
              <field.TextInput
                label={t(
                  'domain.mitarbeiter.create.steps.person.persoenliches.form.e2nId.label',
                )}
                maxLength={13}
                infoText={t(
                  'domain.mitarbeiter.create.steps.person.persoenliches.form.e2nId.info',
                )}
              />
            )}
          </form.AppField>
        </FieldGroup>
      </div>
    </div>
  );
}
